/**
 * The practice outbox (V9-w): recordings that have been made but not yet
 * handed to the server, kept in IndexedDB so a reload does not lose them.
 *
 * Until now the take lived only in `useAudio`'s React state and its
 * `blob:` URL. Closing the tab — or the browser discarding a backgrounded
 * one, which is the common case on a phone — threw the recording away, and
 * the offline banner's promise that "anything you recorded stays right
 * here" was only true for as long as the page stayed alive. IndexedDB is
 * the one web store that takes a `Blob` as it is: `localStorage` would
 * mean base64, a third more bytes and a synchronous main-thread write of a
 * whole WAV.
 *
 * One record per recording, keyed by its `clientAttemptId` — the id the
 * upload carries (V9-b), so the same take never becomes two attempt rows
 * however often it is retried:
 *
 *   { id, lessonId, sentenceIndex, blob, filename, recordedSeconds, createdAt }
 *
 * plus, for the last take of a practice that repeats (R13), `localReps` and
 * `localPracticeSeconds` — what the upload reports about the repetitions
 * before it, so a take sent after a reload still reports them.
 *
 * The store is deliberately small and dumb. It does not retry by itself:
 * the send is the learner's own press of «ارسال برای تحلیل», the same as
 * before, and what this adds is that the take is still there to press it
 * on after a reload. Nothing here touches React, the network or the
 * microphone — the factory is injected so the rules can be tested against
 * a fake `indexedDB`.
 *
 * Everything degrades to a no-op rather than throwing: server rendering
 * has no `indexedDB` at all, and a private window or blocked site data can
 * refuse `open()` at any moment. A learner whose browser will not keep the
 * take still gets exactly the old behaviour — the take in memory — rather
 * than a broken practice screen.
 */

export const OUTBOX_DB = 'tooti-outbox'
export const OUTBOX_STORE = 'takes'
const OUTBOX_VERSION = 1

/**
 * A fresh `clientAttemptId` for one recording.
 *
 * The backend takes 1–64 characters of `A-Z a-z 0-9 _ -`, which a UUID
 * satisfies. `crypto.randomUUID` needs a secure context: the app is only
 * ever served over HTTPS (or localhost), but a take is far too important to
 * lose to a missing global, so a plain random string stands in where it is
 * absent.
 */
export function newTakeId() {
  const webCrypto = globalThis.crypto
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID()
  return `take-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** One IDB request as a promise. */
function requested(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('indexeddb request failed'))
  })
}

/** A kept take older than this is not offered again (PM audit 2026-10-14: a morning take came back in the afternoon). */
export const OUTBOX_MAX_AGE_MS = 24 * 60 * 60 * 1000

/**
 * The newest unsent take for one sentence, or null.
 *
 * Pure, so the "which take comes back when the page reopens" rule is
 * testable without a database. Newest wins: a learner who recorded twice
 * before the page went away is offered the one they would have sent.
 *
 * With `userId`, only that learner's takes (a take with no owner — kept
 * before this rule — belongs to nobody); with `now`, only takes younger than
 * {@link OUTBOX_MAX_AGE_MS}.
 */
export function newestFor(records, lessonId, sentenceIndex, { userId, now } = {}) {
  const mine = (records ?? []).filter(
    (record) =>
      record?.lessonId === lessonId &&
      record?.sentenceIndex === sentenceIndex &&
      (userId === undefined || record.userId === userId) &&
      (now === undefined || now - (record.createdAt ?? 0) <= OUTBOX_MAX_AGE_MS),
  )
  if (mine.length === 0) return null
  return mine.reduce((newest, record) => ((record.createdAt ?? 0) > (newest.createdAt ?? 0) ? record : newest))
}

/**
 * The outbox over one IndexedDB database.
 *
 * `factory` defaults to the browser's own `indexedDB`; the tests hand in a
 * fake one. Without a factory every method still answers — `all()` with an
 * empty list, the writes with `false` — so callers need no second path.
 */
export function createOutbox({ factory = globalThis.indexedDB, name = OUTBOX_DB } = {}) {
  let opening = null

  function open() {
    if (!factory) return Promise.resolve(null)
    opening =
      opening ??
      new Promise((resolve, reject) => {
        const request = factory.open(name, OUTBOX_VERSION)
        request.onupgradeneeded = () => {
          const db = request.result
          if (!db.objectStoreNames.contains(OUTBOX_STORE)) db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' })
        }
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error ?? new Error('indexeddb unavailable'))
      }).catch(() => {
        // A browser that refuses the database (private window, blocked site
        // data) is not an error the practice screen can act on. Forget the
        // rejection so a later call may try again.
        opening = null
        return null
      })
    return opening
  }

  async function withStore(mode, work) {
    const db = await open()
    if (!db) return null
    try {
      const transaction = db.transaction(OUTBOX_STORE, mode)
      return await work(transaction.objectStore(OUTBOX_STORE))
    } catch {
      return null
    }
  }

  return {
    /** Keep (or replace) one recording. Answers whether it was actually stored. */
    async save(record) {
      if (!record?.id) return false
      const done = await withStore('readwrite', (store) => requested(store.put({ ...record })))
      return done !== null
    },

    /** Every unsent recording, newest first. */
    async all() {
      const rows = await withStore('readonly', (store) => requested(store.getAll()))
      return (rows ?? []).slice().sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    },

    /** The newest unsent take for one sentence, or null — of `userId`, younger than a day when they are given. */
    async pendingFor(lessonId, sentenceIndex, scope = {}) {
      return newestFor(await this.all(), lessonId, sentenceIndex, scope)
    },

    /** Drop one recording — it reached the server (scored or queued), or the learner discarded it. */
    async remove(id) {
      if (!id) return false
      const done = await withStore('readwrite', (store) => requested(store.delete(id)))
      return done !== null
    },

    /** Empty the outbox (sign-out, tests). */
    async clear() {
      const done = await withStore('readwrite', (store) => requested(store.clear()))
      return done !== null
    },
  }
}

/** The app's one outbox. */
export const outbox = createOutbox()
